import {
    classifyMarkdownLine,
    isMarkdownTableRow,
    parseMarkdownTableBlock,
    renderMarkdownTable,
    type MarkdownLine,
    type MarkdownRenderOptions,
} from "./markdown-renderer.js";

export type CatLineKind =
    | "plain"
    | "title"
    | "dim"
    | "bright"
    | "rich"
    | "url"
    | "label-url"
    | "label-email"
    | "label-file";

export interface CatTextSegment {
    text: string;
    bright?: boolean;
}

export interface CatLineRender {
    kind: CatLineKind;
    text: string;
    url?: string;
    linkLabel?: string;
    prefix?: string;
    command?: string;
    segments?: CatTextSegment[];
    compact?: boolean;
}

export interface CatFormatOptions extends MarkdownRenderOptions {}

const SECTION_TITLES = new Set(["about", "projects", "contact", "certs"]);
const LOCAL_FILE_RE = /^[\w.-]+\.\w+$/;

function parseListLine(line: string): { bullet: string; body: string } {
    const match = line.match(/^(\s*-\s+)(.+)$/);
    if (match) {
        return { bullet: match[1]!, body: match[2]! };
    }
    return { bullet: "", body: line };
}

function labelPrefix(label: string, bullet: string): string {
    return `${bullet}${label}: `;
}

function parseLabelPrefix(body: string): { label: string; rest: string } | null {
    const boldMatch = body.match(/^\*\*([^*]+):\*\*\s*(.*)$/);
    if (boldMatch) {
        return { label: boldMatch[1]!.trim(), rest: boldMatch[2]! };
    }

    const plainMatch = body.match(/^([^:]+):\s*(.*)$/);
    if (plainMatch) {
        return { label: plainMatch[1]!.trim(), rest: plainMatch[2]! };
    }

    return null;
}

function parseLabelMarkdownLink(body: string): { label: string; linkLabel: string; target: string } | null {
    const labeled = parseLabelPrefix(body);
    if (!labeled) return null;

    const linkMatch = labeled.rest.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!linkMatch) return null;

    return {
        label: labeled.label,
        linkLabel: linkMatch[1]!,
        target: linkMatch[2]!.trim(),
    };
}

function localFileCommand(filename: string): string {
    return filename.toLowerCase().endsWith(".pdf") ? `wget ${filename}` : `cat ${filename}`;
}

function renderFromMarkdownTarget(
    line: string,
    prefix: string,
    linkLabel: string,
    target: string,
): CatLineRender | null {
    if (LOCAL_FILE_RE.test(target)) {
        return {
            kind: "label-file",
            text: line,
            prefix,
            linkLabel,
            command: localFileCommand(target),
        };
    }

    if (target.startsWith("mailto:")) {
        return {
            kind: "label-email",
            text: line,
            prefix,
            linkLabel,
            url: target,
        };
    }

    if (/^https?:\/\//.test(target)) {
        return {
            kind: "label-url",
            text: line,
            prefix,
            linkLabel,
            url: target,
        };
    }

    return null;
}

function markdownToCatLine(line: MarkdownLine): CatLineRender {
    switch (line.kind) {
        case "heading":
            return { kind: "title", text: line.text };
        case "blockquote":
            return { kind: "bright", text: line.text };
        case "rich":
            return {
                kind: "rich",
                text: line.text,
                segments: line.segments?.map((segment) => ({
                    text: segment.text,
                    bright: segment.emphasis,
                })),
            };
        case "table-border":
            return { kind: "dim", text: line.text, compact: line.compact };
        case "table-header":
            return { kind: "bright", text: line.text, compact: line.compact };
        case "table-row":
            return { kind: "plain", text: line.text, compact: line.compact };
        default:
            return { kind: "plain", text: line.text };
    }
}

function classifyPortfolioLine(line: string): CatLineRender | null {
    const { bullet, body } = parseListLine(line);

    const markdownLink = parseLabelMarkdownLink(body);
    if (markdownLink) {
        const rendered = renderFromMarkdownTarget(
            line,
            labelPrefix(markdownLink.label, bullet),
            markdownLink.linkLabel,
            markdownLink.target,
        );
        if (rendered) return rendered;
    }

    const inlineLink = body.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (inlineLink) {
        const rendered = renderFromMarkdownTarget(
            line,
            bullet,
            inlineLink[1]!,
            inlineLink[2]!.trim(),
        );
        if (rendered) return rendered;
    }

    const labeled = parseLabelPrefix(body);
    if (labeled) {
        const prefix = labelPrefix(labeled.label, bullet);
        const { rest } = labeled;

        const plainFile = rest.match(/^([\w.-]+\.\w+)$/i);
        if (plainFile) {
            return {
                kind: "label-file",
                text: line,
                prefix,
                linkLabel: plainFile[1]!,
                command: localFileCommand(plainFile[1]!),
            };
        }

        const plainEmail = rest.match(/^([^\s]+@[^\s]+)$/);
        if (plainEmail) {
            const address = plainEmail[1]!;
            return {
                kind: "label-email",
                text: line,
                prefix,
                linkLabel: address,
                url: `mailto:${address}`,
            };
        }

        const plainUrl = rest.match(/^(https?:\/\/\S+)$/);
        if (plainUrl) {
            const url = plainUrl[1]!;
            return {
                kind: "label-url",
                text: line,
                prefix,
                url,
                linkLabel: url.replace(/^https?:\/\//, ""),
            };
        }
    }

    if (SECTION_TITLES.has(line.toLowerCase())) {
        return { kind: "title", text: line };
    }

    if (/^=+$/.test(line)) {
        return { kind: "dim", text: line };
    }

    if (/^https?:\/\/\S+$/.test(body)) {
        return { kind: "url", text: line, url: body };
    }

    const fileRefMatch = line.match(/^(see\s+)([\w.-]+\.\w+)$/i);
    if (fileRefMatch) {
        const filename = fileRefMatch[2]!;
        return {
            kind: "label-file",
            text: line,
            prefix: fileRefMatch[1]!,
            linkLabel: filename,
            command: `cat ${filename}`,
        };
    }

    if (/^Welcome to /.test(line)) {
        return { kind: "title", text: line };
    }

    return null;
}

export function classifyCatLine(line: string): CatLineRender {
    if (!line) {
        return { kind: "plain", text: "" };
    }

    const portfolio = classifyPortfolioLine(line);
    if (portfolio) {
        return portfolio;
    }

    return markdownToCatLine(classifyMarkdownLine(line));
}

export function isProjectDescription(line: string, previousLine: string | null): boolean {
    if (!previousLine?.startsWith("> ")) return false;
    if (!line) return false;
    if (line.startsWith("> ")) return false;
    if (/^https?:\/\//.test(line)) return false;
    return true;
}

export function expandCatLines(lines: string[], options: CatFormatOptions = {}): CatLineRender[] {
    const renders: CatLineRender[] = [];
    let previousLine: string | null = null;

    for (let index = 0; index < lines.length; ) {
        const line = lines[index]!;

        if (!line.trim()) {
            renders.push({ kind: "plain", text: "" });
            previousLine = line;
            index++;
            continue;
        }

        if (isMarkdownTableRow(line)) {
            const tableLines: string[] = [];
            while (index < lines.length && isMarkdownTableRow(lines[index]!)) {
                tableLines.push(lines[index]!);
                index++;
            }

            const table = parseMarkdownTableBlock(tableLines);
            if (table) {
                for (const markdownLine of renderMarkdownTable(table.header, table.rows, options)) {
                    renders.push(markdownToCatLine(markdownLine));
                }
                previousLine = tableLines[tableLines.length - 1] ?? null;
                continue;
            }

            for (const tableLine of tableLines) {
                renders.push(classifyCatLine(tableLine));
                previousLine = tableLine;
            }
            continue;
        }

        if (isProjectDescription(line, previousLine)) {
            renders.push({ kind: "dim", text: line });
        } else {
            renders.push(classifyCatLine(line));
        }

        previousLine = line;
        index++;
    }

    return renders;
}
